<template>
  <div class="hilo-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.hilo.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.hilo.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- House Edge -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_house_edge') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_house_edge') }}
            <input
              v-model.number="form.house_edge_pct"
              type="number"
              step="0.5"
              min="1"
              max="10"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_house_edge') }}</span>
          </label>
          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_rtp') }}</span>
            <span class="rtp-value">{{ rtpDisplay }}%</span>
          </div>
        </div>
      </section>

      <!-- Deck Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.hilo.section_deck_rules') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.hilo.label_deck_mode') }}
            <select v-model="form.deck_mode" class="form-select">
              <option value="single">Single deck (shuffle per game)</option>
              <option value="fresh">Fresh deck (reshuffle each card reveal)</option>
            </select>
            <span class="form-hint">Single deck: probability tracks remaining cards. Fresh: all 52 cards available every reveal.</span>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.hilo.label_ace_rules') }}
            <select v-model="form.ace_rules" class="form-select">
              <option value="high">High (Ace = 14, beats all face cards)</option>
              <option value="low">Low (Ace = 1, below 2)</option>
              <option value="wild">Wild (Ace counts for both higher and lower)</option>
            </select>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.hilo.label_tie_handling') }}
            <select v-model="form.tie_handling" class="form-select">
              <option value="lose">Lose (wrong higher/lower guess on matching rank = instant loss)</option>
              <option value="push">Push (matching rank on higher/lower = replay, no win/loss)</option>
              <option value="win">Win (matching rank on higher/lower = count as correct)</option>
            </select>
            <span class="form-hint">Applies when the next card has the same rank as the current card and the player guessed higher or lower.</span>
          </label>
        </div>
      </section>

      <!-- Game Options -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.hilo.section_game_options') }}</h3>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.same_bet_enabled" type="checkbox" class="toggle-checkbox" />
            <span>{{ $t('admin.config.hilo.label_same_bet') }}</span>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.hilo.label_max_consecutive') }}
            <input
              v-model.number="form.max_consecutive_guesses"
              type="number"
              step="1"
              min="0"
              max="52"
              class="form-input"
              required
            />
            <span class="form-hint">0 = unlimited (up to 51 remaining cards). 1–52 caps the streak.</span>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.hilo.label_card_style') }}
            <select v-model="form.card_style" class="form-select">
              <option value="classic">Classic (standard playing card look)</option>
              <option value="modern">Modern (stylized flat design)</option>
            </select>
          </label>
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_min_bet') }}
            <input
              v-model.number="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.label_max_bet') }}
            <input
              v-model.number="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_max_payout') }}
            <input
              v-model.number="form.max_payout"
              type="number"
              step="0.01"
              min="0"
              class="form-input"
              required
            />
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface HiloConfigResponse {
  source: 'database' | 'default'
  house_edge_pct: number
  deck_mode: 'single' | 'fresh'
  ace_rules: 'high' | 'low' | 'wild'
  tie_handling: 'push' | 'lose' | 'win'
  same_bet_enabled: boolean
  max_consecutive_guesses: number
  min_bet: string
  max_bet: string
  max_payout: string
  card_style: 'classic' | 'modern'
  rtp: number
}

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')

const form = reactive({
  house_edge_pct: 1.0,
  deck_mode: 'single' as 'single' | 'fresh',
  ace_rules: 'high' as 'high' | 'low' | 'wild',
  tie_handling: 'lose' as 'push' | 'lose' | 'win',
  same_bet_enabled: true,
  max_consecutive_guesses: 0,
  min_bet: '1.00',
  max_bet: '10000.00',
  max_payout: '0.00',
  card_style: 'classic' as 'classic' | 'modern',
})

const rtpDisplay = computed(() => (100 - form.house_edge_pct).toFixed(2))

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<HiloConfigResponse>('/admin/api/games/hilo/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.hilo.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: HiloConfigResponse) {
  source.value = data.source
  form.house_edge_pct = data.house_edge_pct
  form.deck_mode = data.deck_mode
  form.ace_rules = data.ace_rules
  form.tie_handling = data.tie_handling
  form.same_bet_enabled = data.same_bet_enabled
  form.max_consecutive_guesses = data.max_consecutive_guesses
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.max_payout = data.max_payout
  form.card_style = data.card_style
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<HiloConfigResponse>('/admin/api/games/hilo/config', {
      house_edge_pct: form.house_edge_pct,
      deck_mode: form.deck_mode,
      ace_rules: form.ace_rules,
      tie_handling: form.tie_handling,
      same_bet_enabled: form.same_bet_enabled,
      max_consecutive_guesses: form.max_consecutive_guesses,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      max_payout: form.max_payout,
      card_style: form.card_style,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.hilo.save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.hilo.error_save')
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.hilo-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 680px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input,
.form-select {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* RTP display inline with house edge */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Toggle */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--accent, #1a9e6e);
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
</style>
